/** @jsxImportSource @emotion/react */
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ControlBtnGroup,
  GlobalBtnGroup,
  SubTitleGroup,
  SubTitleLayout,
} from 'components/layouts/ContentLayout';
import { useLoading } from 'components/process/Loading';
import { useMessageBar } from 'hooks/useMessageBar';
import {
  Box,
  Button,
  Checkbox,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { IconButton } from 'components/buttons/IconSVG';
import { tb } from 'components/layouts/Table';
import _ from 'lodash';
import { useCommonModal } from 'hooks/useCommonModal';
import { DefaultProps } from '../MdIvesCmpuRegPage';
import CustomRadio from 'components/inputs/CustomRadio';
import { st } from 'components/inputs/CustomInput';

const IvesResultAnlsPage = ({ elmPrjId }: DefaultProps) => {
  const { t } = useTranslation();
  const { isLoading } = useLoading();
  const { openMessageBar } = useMessageBar();
  const { openCommonModal } = useCommonModal();

  const [version, setVersion] = useState('');
  const [resultData, setResultData] = useState([]);
  const [mainSpec, setMainSpec] = useState({});
  const [summary, setSummary] = useState([]);
  const [rankList, setRankList] = useState([]);

  const layoutDefinition = [
    { title: t('setup.label.공정', '공정'), width: '' },
    { title: t('setup.label.설비군', '설비군'), width: '' },
    { title: t('setup.label.투자항목', '투자항목'), width: '' },
    { title: t('setup.label.설비대수', '설비대수'), width: '' },
    { title: t('setup.label.단가', '단가'), width: '' },
    { title: t('setup.label.투자비', '투자비'), width: '' },
  ];

  const initCodes = () => {
    //
  };

  const btnList = () => {
    //
  };

  const btnReset = () => {
    //
  };

  const btnSave = () => {
    //
  };

  const handleOnChange = (name: string, value: any) => {
    //
  };

  useEffect(() => {
    initCodes();
  }, []);

  return (
    <>
      <SubTitleLayout>
        <div>
          <CustomRadio
            id="ver-radio1"
            label={'Ford 1.0'}
            name="version"
            value={'1'}
            checked={version === '1'}
            onChange={(e) => handleOnChange('version', e.target.value)}
          />
          <CustomRadio
            id="ver-radio2"
            label={'Ford 2.0'}
            name="version"
            value={'2'}
            checked={version === '2'}
            onChange={(e) => handleOnChange('version', e.target.value)}
          />
          <CustomRadio
            id="ver-radio3"
            label={'Ford 3.0'}
            name="version"
            value={'3'}
            checked={version === '3'}
            onChange={(e) => handleOnChange('version', e.target.value)}
          />
        </div>
        <GlobalBtnGroup>
          <Button css={IconButton.button} className="icoNone" disableRipple onClick={btnList}>
            {t('com.button.목록', '목록')}
          </Button>
          <Button css={IconButton.button} className="save" disabled={isLoading} onClick={btnSave}>
            {t('com.label.저장', '저장')}
          </Button>
        </GlobalBtnGroup>
      </SubTitleLayout>
      <Grid container sx={{ gap: 0 }}>
        <Grid item xs={12} md={6} sx={{ pr: { md: 2 } }}>
          <SubTitleLayout>
            <SubTitleGroup>
              <h3>{t('setup.label.형개발 투자 검토 결과 요약', '형개발 투자 검토 결과 요약')}</h3>
            </SubTitleGroup>
          </SubTitleLayout>
          <TableContainer css={tb.tableCol}>
            <Table>
              <TableHead>
                <TableRow>
                  {layoutDefinition.map((item, idx) => {
                    return (
                      <TableCell key={idx} width={item.width}>
                        {item.title}
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {Array(10).map((item, idx) => {
                  return (
                    <TableRow key={idx}>
                      <TableCell>{idx}</TableCell>
                      <TableCell>{idx}</TableCell>
                      <TableCell>{idx}</TableCell>
                      <TableCell>{idx}</TableCell>
                      <TableCell>{idx}</TableCell>
                      <TableCell>{idx}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
        <Grid item xs={12} md={6} sx={{ pr: { md: 2 } }}>
          <SubTitleLayout>
            <SubTitleGroup>
              <h3>{t('setup.label.형개발 투자비 요약', '형개발 투자비 요약')}</h3>
            </SubTitleGroup>
            <ControlBtnGroup>
              <label htmlFor={`main-model-chk`}>
                {t('setup.label.대표모델 지정', '대표모델 지정')}
              </label>
              <div className="txt">
                <Checkbox
                  id={`main-model-chk`}
                  css={st.checkbox}
                  className="custom"
                  value={'ISU_EXCP'}
                  onChange={(e, c) => {
                    handleOnChange('procChkCd', c ? e.target.value : '');
                  }}
                />
              </div>
            </ControlBtnGroup>
          </SubTitleLayout>
          <Box sx={{ p: 2, border: '2px solid', borderColor: 'divider' }}>
            <SubTitleLayout>
              <SubTitleGroup>
                <h3>{t('setup.label.주요 사항', '주요 사항')}</h3>
              </SubTitleGroup>
              <SubTitleGroup>
                <h3>
                  {t('setup.label.유사 모델', '유사 모델')} : <span>H5_5A</span>
                </h3>
              </SubTitleGroup>
            </SubTitleLayout>
            <TableContainer css={tb.customTable}>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell className="header" rowSpan={5}>
                      완성 Cell
                    </TableCell>
                    <TableCell className="header">조성</TableCell>
                    <TableCell className="body"></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="header">양/단 방향</TableCell>
                    <TableCell className="body"></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="header">전장</TableCell>
                    <TableCell className="body"></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="header">전폭</TableCell>
                    <TableCell className="body"></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="header">두께</TableCell>
                    <TableCell className="body"></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="header">PKG</TableCell>
                    <TableCell className="header">3면/4면</TableCell>
                    <TableCell className="body"></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="header">EOL</TableCell>
                    <TableCell className="header">Folding Type</TableCell>
                    <TableCell className="body"></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            <SubTitleLayout>
              <SubTitleGroup>
                <h3>{t('setup.label.투자비 요약', '투자비 요약')}</h3>
              </SubTitleGroup>
            </SubTitleLayout>
            <TableContainer css={tb.tableCol}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>공정</TableCell>
                    <TableCell>투자비</TableCell>
                    <TableCell>설비대수</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>PKG</TableCell>
                    <TableCell>3면/4면</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>EOL</TableCell>
                    <TableCell>Folding Type</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            <SubTitleLayout>
              <SubTitleGroup>
                <h3>{t('setup.label.투자항목별 Top 5', '투자항목별 Top 5')}</h3>
              </SubTitleGroup>
            </SubTitleLayout>
            <TableContainer css={tb.tableCol}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>No</TableCell>
                    <TableCell>공정</TableCell>
                    <TableCell>설비군</TableCell>
                    <TableCell>투자항목</TableCell>
                    <TableCell>설비대수</TableCell>
                    <TableCell>단가</TableCell>
                    <TableCell>투자비</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

export default IvesResultAnlsPage;
